(function () {
  'use strict';

  var config = window.bloomingfulCartMonitorConfig || {};
  var endpoint = String(config.endpoint || '').replace(/\/$/, '');
  var started = false;
  var captureTimers = {};
  var observer = null;
  var lastLocalHash = '';
  var pendingRequest = null;
  var clientId = '';
  var visitState = null;
  var visitPayloadPromise = null;
  var customerActivityTimer = null;
  var lastInteractionAt = Date.now();
  var lastActivityHeartbeatAt = Date.now();
  var VISIT_TIMEOUT_MS = 30 * 60 * 1000;
  var ACTIVE_HEARTBEAT_MS = 30 * 1000;
  var ACTIVE_INTERACTION_WINDOW_MS = 2 * 60 * 1000;
  var CUSTOMER_OBSERVATION_KEY = 'bloomingful:customerObservation';
  var DISCOUNT_ATTEMPTS_KEY = 'bloomingful:cartMonitorDiscountAttempts';
  var DISCOUNT_ATTEMPT_TTL_MS = 2 * 60 * 60 * 1000;

  if (!endpoint || typeof window.fetch !== 'function' || !window.crypto || !window.crypto.subtle) return;

  function initialize() {
    if (started || !analyticsAllowed()) return;
    started = true;
    clientId = getClientId();
    visitState = getVisitState();
    getVisitPayload();
    initializeCustomerActivity();
    bindEvents();
    initObserver();
    scheduleCapture(visitState.is_new_visit ? 'visit_started' : 'page_viewed', 0);
    window.setTimeout(function () { scheduleCapture('pricing_settled', 0); }, 1800);
    window.setTimeout(function () { scheduleCapture('pricing_settled', 0); }, 4300);
  }

  function analyticsAllowed() {
    try {
      return Boolean(window.Shopify && window.Shopify.customerPrivacy && window.Shopify.customerPrivacy.analyticsProcessingAllowed());
    } catch (error) {
      return false;
    }
  }

  function loadPrivacyApi() {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      initialize();
      return;
    }
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
      if (!error) initialize();
    });
  }

  function bindEvents() {
    ['ajaxProduct:added', 'cart:rebuilt', 'cart:quantity', 'cart:build', 'cart:updated'].forEach(function (name) {
      document.addEventListener(name, function () {
        scheduleCapture(name.replace(/[:]/g, '_'), 450);
        window.setTimeout(function () { scheduleCapture('pricing_settled', 0); }, 1700);
        window.setTimeout(function () { scheduleCapture('pricing_settled', 0); }, 4100);
      });
    });

    document.addEventListener('change', function (event) {
      if (event.target && event.target.matches('#CartDrawerForm [name="updates[]"], #CartPageForm [name="updates[]"]')) {
        scheduleCapture('quantity_changed', 650);
      }
      if (isDiscountInput(event.target)) {
        captureDiscountAttemptsFromPage();
        scheduleCapture('discount_code_entered', 100);
      }
    });

    document.addEventListener('click', function (event) {
      if (event.target && event.target.closest('[data-cart-remove], .js-qty__adjust')) {
        scheduleCapture('cart_control_clicked', 850);
      }
      if (event.target && event.target.closest('[name="checkout"], [data-cart-checkout], a[href*="/checkout"]')) {
        captureDiscountAttemptsFromPage();
        scheduleCapture('checkout_clicked', 0);
      }
    });

    document.addEventListener('submit', function (event) {
      if (!event.target || !event.target.matches || !event.target.matches('#CartDrawerForm, #CartPageForm')) return;
      captureDiscountAttemptsFromPage();
      scheduleCapture('cart_submitted', 0);
    });
  }

  function initObserver() {
    if (observer) return;
    observer = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (mutation) {
        var target = mutation.target && mutation.target.nodeType === 1 ? mutation.target : mutation.target && mutation.target.parentElement;
        return target && target.closest && target.closest('#CartDrawer, #CartPageForm');
      });
      if (relevant) scheduleCapture('cart_rendered', 700);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  }

  function scheduleCapture(eventHint, delay) {
    window.clearTimeout(captureTimers[eventHint]);
    captureTimers[eventHint] = window.setTimeout(function () {
      delete captureTimers[eventHint];
      capture(eventHint);
    }, Number(delay) || 0);
  }

  async function capture(eventHint) {
    if (!analyticsAllowed()) return;
    if (pendingRequest) {
      try { await pendingRequest; } catch (error) {}
    }
    pendingRequest = fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    var response;
    try {
      response = await pendingRequest;
    } catch (error) {
      pendingRequest = null;
      return;
    }
    pendingRequest = null;
    if (!response.ok) return;
    var cart;
    try {
      cart = await response.json();
    } catch (error) {
      return;
    }
    if (!cart.token) return;
    window.cart_data = cart;

    var cartTokenHash = await sha256(cart.token);
    try { window.localStorage.setItem('bloomingful:cartTokenHash', cartTokenHash); } catch (error) {}
    var customer = customerPayload();
    try { window.localStorage.setItem('bloomingful:pricingGroups', JSON.stringify(customer.pricing_groups || [])); } catch (error) {}

    var surfaces = [collectSurface('drawer', '#CartDrawerForm'), collectSurface('full_cart', '#CartPageForm')].filter(Boolean);
    var discountObservations = collectDiscountObservations();
    var discountAttempts = rememberDiscountAttempts(cartTokenHash, discountObservations);
    var payload = {
      event_id: randomId(),
      event_at: new Date().toISOString(),
      event_hint: eventHint,
      client_id: clientId,
      shop_domain: config.shopDomain,
      theme_id: String(config.themeId || ''),
      cart_token_hash: cartTokenHash,
      page_path: safePagePath(),
      surface: visibleSurface(surfaces),
      customer: customer,
      location: accountLocation(),
      page: pagePayload(),
      visit: await getVisitPayload(),
      discount_codes: discountCodes(discountObservations),
      discount_attempts: discountAttempts,
      runtime: runtimePayload(),
      cart: {
        currency: cart.currency || config.currency || 'USD',
        item_count: cart.item_count,
        original_total_price: cart.original_total_price,
        total_price: cart.total_price,
        total_discount: cart.total_discount,
        items: (cart.items || []).map(normalizeCartItem)
      },
      surfaces: surfaces
    };
    var localHash = await sha256(JSON.stringify(payload.cart) + JSON.stringify(payload.surfaces));
    if (localHash === lastLocalHash && eventHint === 'cart_rendered') return;
    lastLocalHash = localHash;
    send(payload);
  }

  function initializeCustomerActivity() {
    var customer = customerPayload();
    var now = Date.now();
    var previous = parseStoredJson(readStorage(CUSTOMER_OBSERVATION_KEY)) || {};

    if (!customer.id) {
      writeStorage(CUSTOMER_OBSERVATION_KEY, JSON.stringify({
        customer_id: '',
        last_anonymous_at: new Date(now).toISOString(),
        last_seen_at: new Date(now).toISOString()
      }));
      return;
    }

    sendCustomerActivity(visitState && visitState.is_new_visit ? 'signed_in_visit_started' : 'signed_in_page_viewed');
    var anonymousAt = Date.parse(previous.last_anonymous_at || '');
    if (
      previous.customer_id !== customer.id &&
      Number.isFinite(anonymousAt) &&
      now - anonymousAt >= 0 &&
      now - anonymousAt <= VISIT_TIMEOUT_MS
    ) {
      sendCustomerActivity('customer_login_observed');
    }

    writeStorage(CUSTOMER_OBSERVATION_KEY, JSON.stringify({
      customer_id: customer.id,
      last_anonymous_at: '',
      last_seen_at: new Date(now).toISOString()
    }));
    bindCustomerActivityHeartbeat();
  }

  function bindCustomerActivityHeartbeat() {
    if (customerActivityTimer) return;
    ['pointerdown', 'touchstart', 'keydown', 'scroll'].forEach(function (name) {
      document.addEventListener(name, markCustomerActive, { passive: true });
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') {
        lastInteractionAt = Date.now();
        lastActivityHeartbeatAt = Date.now();
      } else {
        flushCustomerActivity('visibility_hidden');
      }
    });
    window.addEventListener('pagehide', function () { flushCustomerActivity('page_hidden'); });
    customerActivityTimer = window.setInterval(function () { flushCustomerActivity('heartbeat'); }, ACTIVE_HEARTBEAT_MS);
  }

  function markCustomerActive() {
    lastInteractionAt = Date.now();
  }

  function flushCustomerActivity(interactionType) {
    var now = Date.now();
    var elapsedSeconds = Math.min(60, Math.max(0, Math.round((now - lastActivityHeartbeatAt) / 1000)));
    var recentlyActive = now - lastInteractionAt <= ACTIVE_INTERACTION_WINDOW_MS;
    var visible = document.visibilityState !== 'hidden';
    lastActivityHeartbeatAt = now;
    if (!recentlyActive || (!visible && interactionType === 'heartbeat') || elapsedSeconds < 5) return;
    sendCustomerActivity('session_activity', {
      active_seconds_delta: elapsedSeconds,
      page_visible: visible,
      interaction_type: interactionType
    });
  }

  async function sendCustomerActivity(eventType, sessionActivity) {
    if (!analyticsAllowed()) return;
    var customer = customerPayload();
    if (!customer.id) return;
    var payload = {
      event_id: randomId(),
      event_type: eventType,
      event_at: new Date().toISOString(),
      client_id: clientId,
      shop_domain: config.shopDomain,
      theme_id: String(config.themeId || ''),
      page_path: safePagePath(),
      customer: customer,
      location: accountLocation(),
      page: pagePayload(),
      visit: await getVisitPayload()
    };
    if (sessionActivity) payload.session_activity = sessionActivity;
    window.fetch(endpoint + '/api/cart-monitor/customer-activity', {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).catch(function () {});
  }
  function runtimePayload() {
    return {
      wsaio_present: Boolean(window.WSAIO),
      wsaio_checkout_present: Boolean(window.WSAIO && typeof window.WSAIO.checkout === 'function'),
      wholesale_pricing_access: Boolean(window.bloomingfulCustomerHasWholesaleAccess || window.bloomingfulPublicWs01Event),
      fallback_priced_lines: document.querySelectorAll('[data-bf-wsaio-resolved-unit-cents]').length,
      checkout_guard_visible: Boolean(document.querySelector('.bf-wsaio-runtime-pending')),
      compat_version: String(window.bloomingfulWsaioCompatVersion || '')
    };
  }

  function collectSurface(name, selector) {
    var form = document.querySelector(selector);
    if (!form) return null;
    var items = [];
    form.querySelectorAll('.cart__item').forEach(function (element) {
      var priceColumn = element.querySelector('.cart__item-price-col');
      var visiblePrice = priceColumn && (priceColumn.querySelector('.bf-cart-discount-price') || priceColumn.querySelector('.cart__discount:not(small)') || priceColumn.querySelector('.cart__price:not(.cart__price--strikethrough)'));
      var comparePrice = priceColumn && (priceColumn.querySelector('.bf-cart-compare-price') || priceColumn.querySelector('.cart__price--strikethrough'));
      var quantity = element.querySelector('[name="updates[]"], .js-qty__num');
      var key = element.getAttribute('data-key') || '';
      var cartItem = (window.cart_data && window.cart_data.items || []).filter(function (item) { return String(item.key) === String(key); })[0] || {};
      items.push({
        key: key,
        variant_id: String(cartItem.variant_id || ''),
        sku: String(cartItem.sku || ''),
        quantity: Number(quantity && quantity.value || cartItem.quantity || 0),
        displayed_unit_price: parseMoney(visiblePrice && visiblePrice.textContent),
        compare_at_unit_price: parseMoney(comparePrice && comparePrice.textContent),
        expected_wsaio_unit_price: Number(element.dataset.bfWsaioResolvedUnitCents || 0),
        price_text: cleanText(priceColumn && priceColumn.textContent, 240)
      });
    });
    var subtotal = form.querySelector('[data-subtotal]');
    var discount = form.querySelector('[data-bf-wsaio-discount]');
    var discountText = Array.prototype.map.call(form.querySelectorAll('[data-discounts], [data-wsaio-messages], .bf-wsaio-applied-summary'), function (element) {
      return cleanText(element.textContent, 250);
    }).filter(Boolean).join(' | ');
    return {
      name: name,
      visible: isVisible(form),
      visible_subtotal: parseMoney(subtotal && subtotal.textContent),
      visible_discount: parseMoney(discount && discount.textContent),
      subtotal_text: cleanText(subtotal && subtotal.textContent, 120),
      discount_text: cleanText(discountText, 500),
      items: items
    };
  }

  function normalizeCartItem(item) {
    return {
      key: item.key,
      product_id: item.product_id,
      variant_id: item.variant_id,
      sku: item.sku,
      product_title: item.product_title,
      variant_title: item.variant_title,
      quantity: item.quantity,
      price: item.price,
      original_price: item.original_price,
      discounted_price: item.discounted_price,
      final_price: item.final_price,
      line_price: item.line_price,
      original_line_price: item.original_line_price,
      final_line_price: item.final_line_price,
      total_discount: item.total_discount,
      image_url: item.image,
      url: item.url,
      properties: item.properties,
      discount_allocations: item.line_level_discount_allocations
    };
  }

  function customerPayload() {
    var customer = window.bloomingfulCustomerInfo || {};
    return {
      id: customer.id || '',
      email: customer.email || '',
      first_name: customer.firstName || '',
      last_name: customer.lastName || '',
      name: customer.name || '',
      company: customer.company || '',
      identity_source: customer.id ? 'shopify_account' : 'anonymous',
      pricing_groups: pricingGroups(customer.tags || [])
    };
  }

  function accountLocation() {
    var customer = window.bloomingfulCustomerInfo || {};
    return {
      account_city: customer.accountCity || '',
      account_region: customer.accountRegion || '',
      account_country: customer.accountCountry || ''
    };
  }

  function pricingGroups(tags) {
    var normalizedTags = (Array.isArray(tags) ? tags : []).map(function (tag) { return String(tag || '').trim().toLowerCase(); });
    var ruleMap = window.bloomingfulWsaioCustomerGroups || {};
    var groups = [];
    (ruleMap.rules || []).forEach(function (rule) {
      if (String(rule.customerGroup || '').toLowerCase() !== 'has_tag') return;
      (rule.tags || []).forEach(function (tag) {
        var normalized = String(tag || '').trim().toLowerCase();
        if (normalized && normalized !== 'all' && normalizedTags.indexOf(normalized) !== -1) groups.push(String(tag).toUpperCase());
      });
    });
    return groups.filter(function (group, index) { return groups.indexOf(group) === index; });
  }

  function collectDiscountObservations() {
    var observations = [];
    document.querySelectorAll('#CartDrawerForm input[name="discount"], #CartPageForm input[name="discount"]').forEach(function (input) {
      if (!discountInputIsSafe(input)) return;
      addDiscountObservation(observations, input.value, 'cart_input');
    });
    document.querySelectorAll('#CartDrawerForm [data-wsaio-messages], #CartPageForm [data-wsaio-messages], .bf-wsaio-applied-summary').forEach(function (element) {
      var expression = /discount\s+code:\s*([A-Z0-9_-]+)/ig;
      var match;
      while ((match = expression.exec(String(element.textContent || '')))) addDiscountObservation(observations, match[1], 'wsaio_message');
    });
    try {
      addDiscountObservation(observations, new URLSearchParams(window.location.search).get('discount'), 'discount_parameter');
    } catch (error) {}
    try {
      var affiliatePromo = parseStoredJson(readStorage('bloomingful:affiliatePromo'));
      var affiliateAge = Date.now() - Number(affiliatePromo && affiliatePromo.startedAt || 0);
      if (affiliatePromo && affiliateAge >= 0 && affiliateAge <= 365 * 24 * 60 * 60 * 1000) {
        addDiscountObservation(observations, affiliatePromo.code, 'affiliate_promo');
      }
    } catch (error) {}
    return observations;
  }

  function discountCodes(observations) {
    return (observations || []).filter(function (observation) { return observation.source !== 'discount_parameter'; }).map(function (observation) { return observation.code; }).filter(function (code, index, codes) {
      return code && codes.indexOf(code) === index;
    });
  }

  function captureDiscountAttemptsFromPage() {
    var cartTokenHash = readStorage('bloomingful:cartTokenHash');
    if (!cartTokenHash) return [];
    return rememberDiscountAttempts(cartTokenHash, collectDiscountObservations());
  }

  function rememberDiscountAttempts(cartTokenHash, observations) {
    if (!/^[a-f0-9]{64}$/i.test(String(cartTokenHash || ''))) return [];
    var now = Date.now();
    var existing = parseStoredJson(readStorage(DISCOUNT_ATTEMPTS_KEY));
    var attempts = (Array.isArray(existing) ? existing : []).filter(function (attempt) {
      var capturedAt = Date.parse(attempt && attempt.captured_at || '');
      return cleanDiscountCode(attempt && attempt.code) && Number.isFinite(capturedAt) && now - capturedAt <= DISCOUNT_ATTEMPT_TTL_MS && now - capturedAt >= -60000;
    });

    (observations || []).forEach(function (observation) {
      var code = cleanDiscountCode(observation && observation.code);
      if (!code) return;
      attempts = attempts.filter(function (attempt) {
        return !(attempt.code === code && attempt.cart_token_hash === cartTokenHash && attempt.source === observation.source);
      });
      attempts.push({
        code: code,
        source: observation.source,
        captured_at: new Date(now).toISOString(),
        cart_token_hash: cartTokenHash
      });
    });

    attempts = attempts.slice(-24);
    writeStorage(DISCOUNT_ATTEMPTS_KEY, JSON.stringify(attempts));
    return attempts.filter(function (attempt) { return attempt.cart_token_hash === cartTokenHash; }).slice(-12);
  }

  function addDiscountObservation(observations, value, source) {
    var code = cleanDiscountCode(value);
    if (!code) return;
    if (observations.some(function (observation) { return observation.code === code && observation.source === source; })) return;
    observations.push({ code: code, source: source });
  }

  function cleanDiscountCode(value) {
    var code = String(value || '').trim().toUpperCase();
    return /^[A-Z0-9][A-Z0-9_-]{0,79}$/.test(code) ? code : '';
  }

  function isDiscountInput(element) {
    return Boolean(element && element.matches && element.matches('#CartDrawerForm input[name="discount"], #CartPageForm input[name="discount"]'));
  }

  function discountInputIsSafe(input) {
    if (!input) return false;
    var label = input.labels && input.labels.length ? input.labels[0].textContent || '' : '';
    var context = [input.name, input.placeholder, input.getAttribute('aria-label'), label].filter(Boolean).join(' ');
    return !/gift\s*card/i.test(context);
  }

  function send(payload) {
    window.fetch(endpoint + '/api/cart-monitor/snapshot', {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response || !response.ok) return null;
      return response.json().catch(function () { return null; });
    }).then(function (result) {
      var validation = result && result.pricing_validation;
      if (!validation || validation.status !== 'failed') return;
      document.dispatchEvent(new CustomEvent('bloomingful:pricing-validation-failed', {
        detail: {
          stage: cleanText(validation.stage || 'cart', 40),
          issue_count: Number(validation.issue_count || 0)
        }
      }));
    }).catch(function () {});
  }

  function visibleSurface(surfaces) {
    var visible = surfaces.filter(function (surface) { return surface.visible; });
    return visible.length ? visible.map(function (surface) { return surface.name; }).join('+') : 'background';
  }

  function isVisible(element) {
    return Boolean(element && element.getClientRects().length && window.getComputedStyle(element).visibility !== 'hidden');
  }

  function parseMoney(value) {
    var matches = String(value || '').match(/-?[\d,]+(?:\.\d{1,2})?/g);
    if (!matches || !matches.length) return 0;
    var number = Number(matches[0].replace(/,/g, ''));
    return Number.isFinite(number) ? Math.abs(Math.round(number * 100)) : 0;
  }

  function cleanText(value, maxLength) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, maxLength);
  }

  function pagePayload() {
    return {
      type: cleanText(config.pageType || document.body && document.body.className || '', 80),
      title: cleanText(config.pageTitle || document.title || '', 240),
      path: safePagePath()
    };
  }

  function safePagePath() {
    return cleanText(window.location && window.location.pathname || '/', 500) || '/';
  }

  function getVisitState() {
    var now = Date.now();
    var visitorId = readStorage('bloomingful:cartMonitorVisitorId');
    if (!visitorId) {
      visitorId = randomId();
      writeStorage('bloomingful:cartMonitorVisitorId', visitorId);
    }

    var previous = parseStoredJson(readStorage('bloomingful:cartMonitorVisitState')) || {};
    var lastActivity = Date.parse(previous.last_activity_at || '');
    var isNewVisit = !previous.visit_id || !Number.isFinite(lastActivity) || now - lastActivity >= VISIT_TIMEOUT_MS;
    var state = previous;

    if (isNewVisit) {
      var referrer = safeReferrer(document.referrer);
      state = {
        visitor_id: visitorId,
        visit_id: randomId(),
        visit_number: Math.max(1, Number(previous.visit_number || 0) + 1),
        started_at: new Date(now).toISOString(),
        last_activity_at: new Date(now).toISOString(),
        landing_path: safePagePath(),
        landing_title: cleanText(config.pageTitle || document.title || '', 240),
        referrer_url: referrer.url,
        referrer_domain: referrer.domain,
        search_query: referrer.searchQuery,
        utm_source: safeCampaignValue('utm_source'),
        utm_medium: safeCampaignValue('utm_medium'),
        utm_campaign: safeCampaignValue('utm_campaign'),
        is_new_visit: true
      };
    } else {
      state.visitor_id = visitorId;
      state.last_activity_at = new Date(now).toISOString();
      state.is_new_visit = false;
    }

    writeStorage('bloomingful:cartMonitorVisitState', JSON.stringify(state));
    return state;
  }

  function getVisitPayload() {
    if (visitPayloadPromise) return visitPayloadPromise;
    visitPayloadPromise = Promise.all([
      sha256(visitState && visitState.visitor_id || clientId),
      sha256(visitState && visitState.visit_id || clientId)
    ]).then(function (hashes) {
      var device = devicePayload();
      var payload = {
        visitor_hash: hashes[0],
        visit_hash: hashes[1],
        visit_number: Math.max(1, Number(visitState && visitState.visit_number || 1)),
        started_at: visitState && visitState.started_at || new Date().toISOString(),
        landing_path: visitState && visitState.landing_path || safePagePath(),
        landing_title: visitState && visitState.landing_title || cleanText(config.pageTitle || document.title || '', 240),
        referrer_url: visitState && visitState.referrer_url || '',
        referrer_domain: visitState && visitState.referrer_domain || '',
        search_query: visitState && visitState.search_query || '',
        utm_source: visitState && visitState.utm_source || '',
        utm_medium: visitState && visitState.utm_medium || '',
        utm_campaign: visitState && visitState.utm_campaign || '',
        device_type: device.device_type,
        device_platform: device.device_platform,
        device_browser: device.device_browser,
        viewport_width: device.viewport_width,
        viewport_height: device.viewport_height
      };
      writeStorage('bloomingful:cartMonitorVisit', JSON.stringify(payload));
      return payload;
    });
    return visitPayloadPromise;
  }

  function devicePayload() {
    var userAgent = String(window.navigator && window.navigator.userAgent || '');
    var platform = String(window.navigator && window.navigator.platform || '');
    var touchPoints = Number(window.navigator && window.navigator.maxTouchPoints || 0);
    var viewportWidth = Math.max(0, Math.round(Number(window.innerWidth || document.documentElement && document.documentElement.clientWidth || 0)));
    var viewportHeight = Math.max(0, Math.round(Number(window.innerHeight || document.documentElement && document.documentElement.clientHeight || 0)));
    var isIpadOs = platform === 'MacIntel' && touchPoints > 1;
    var isTablet = isIpadOs || /iPad|Tablet|PlayBook|Silk/i.test(userAgent) || (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent));
    var isMobile = !isTablet && /Mobile|iPhone|iPod|IEMobile|Opera Mini|Android/i.test(userAgent);
    return {
      device_type: isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop',
      device_platform: isIpadOs || /iPad|iPhone|iPod/i.test(userAgent) ? 'iOS / iPadOS' : /Android/i.test(userAgent) ? 'Android' : /Windows/i.test(userAgent) ? 'Windows' : /CrOS/i.test(userAgent) ? 'ChromeOS' : /Mac/i.test(userAgent) ? 'macOS' : /Linux/i.test(userAgent) ? 'Linux' : 'Other',
      device_browser: /SamsungBrowser/i.test(userAgent) ? 'Samsung Internet' : /Edg/i.test(userAgent) ? 'Edge' : /OPR|Opera/i.test(userAgent) ? 'Opera' : /FxiOS|Firefox/i.test(userAgent) ? 'Firefox' : /CriOS|Chrome/i.test(userAgent) ? 'Chrome' : /Safari/i.test(userAgent) ? 'Safari' : 'Other',
      viewport_width: Math.min(viewportWidth, 10000),
      viewport_height: Math.min(viewportHeight, 10000)
    };
  }

  function safeReferrer(value) {
    try {
      var url = new URL(String(value || ''));
      if (!/^https?:$/.test(url.protocol)) return { url: '', domain: '', searchQuery: '' };
      return {
        url: cleanText(url.origin + url.pathname, 500),
        domain: cleanText(url.hostname.replace(/^www\./i, ''), 160),
        searchQuery: searchQueryFromReferrer(url)
      };
    } catch (error) {
      return { url: '', domain: '', searchQuery: '' };
    }
  }

  function searchQueryFromReferrer(url) {
    var host = String(url && url.hostname || '').toLowerCase().replace(/^www\./, '');
    var key = '';
    if (host === 'google.com' || host.endsWith('.google.com') || host.endsWith('.google.co.uk')) key = 'q';
    else if (host === 'bing.com' || host.endsWith('.bing.com')) key = 'q';
    else if (host === 'duckduckgo.com' || host.endsWith('.duckduckgo.com')) key = 'q';
    else if (host === 'search.yahoo.com' || host.endsWith('.search.yahoo.com')) key = 'p';
    else if (host === 'search.brave.com') key = 'q';
    else if (host === 'ecosia.org' || host.endsWith('.ecosia.org')) key = 'q';
    return key ? cleanText(url.searchParams.get(key), 250) : '';
  }

  function safeCampaignValue(name) {
    try {
      return cleanText(new URLSearchParams(window.location.search).get(name), 120);
    } catch (error) {
      return '';
    }
  }

  function parseStoredJson(value) {
    try {
      return JSON.parse(value || '');
    } catch (error) {
      return null;
    }
  }

  function readStorage(key) {
    try {
      return window.localStorage.getItem(key) || '';
    } catch (error) {
      return '';
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {}
  }

  function getClientId() {
    try {
      var current = window.sessionStorage.getItem('bloomingful:cartMonitorClientId');
      if (current) return current;
      current = randomId();
      window.sessionStorage.setItem('bloomingful:cartMonitorClientId', current);
      return current;
    } catch (error) {
      return randomId();
    }
  }

  function randomId() {
    if (window.crypto.randomUUID) return window.crypto.randomUUID();
    var bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.prototype.map.call(bytes, function (byte) { return byte.toString(16).padStart(2, '0'); }).join('');
  }

  async function sha256(value) {
    var data = new TextEncoder().encode(String(value || ''));
    var digest = await window.crypto.subtle.digest('SHA-256', data);
    return Array.prototype.map.call(new Uint8Array(digest), function (byte) { return byte.toString(16).padStart(2, '0'); }).join('');
  }

  document.addEventListener('visitorConsentCollected', initialize);
  loadPrivacyApi();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadPrivacyApi, { once: true });
  }
})();
